import {
  OeTestRunStatus,
  OeTestStatus,
} from '@trustero/trustero-api-web/lib/audit/auditbot_pb'

// Shown while a control's OE procedures are enqueued but not yet running — in
// the live TEST DETAILS body and, mirrored, in the accordion header.
export const OE_QUEUED_NARRATION =
  'Test procedures are queued and will run shortly.'

export const isTerminalStatus = (status: OeTestStatus): boolean => {
  const s = status.getRunStatus()
  return (
    s === OeTestRunStatus.OE_TEST_DONE || s === OeTestRunStatus.OE_TEST_ERROR
  )
}

// A control is "queued" while none of its procedures have started: the OE run
// has enumerated it but the scan hasn't reached it, so every live status is
// PENDING (only the synthesized placeholder). Such a control shows the queued
// hourglass instead of the running spinner.
export const isControlQueued = (statuses: OeTestStatus[]): boolean =>
  statuses.length > 0 &&
  statuses.every((s) => s.getRunStatus() === OeTestRunStatus.OE_TEST_PENDING)

// The live label for a single procedure: its agent narration while running
// (falling back to a round heartbeat), "Loading result…" once terminal but
// awaiting its fetched result, or "Queued" before it starts.
export const getProcedureStatusLabel = (status: OeTestStatus): string => {
  if (isTerminalStatus(status)) return 'Loading result…'
  if (status.getRunStatus() === OeTestRunStatus.OE_TEST_RUNNING) {
    const narration = status.getNarration().trim()
    const round = status.getCurrentRound()
    return narration || (round > 0 ? `Analyzing · Round ${round}` : 'Analyzing')
  }
  return 'Queued'
}

// The control's single "latest status" for the accordion header: the queued
// message while nothing has started, otherwise the label of the most recently
// started running procedure (or "Loading result…" while finished procedures
// fetch their results).
export const getLatestNarration = (statuses: OeTestStatus[]): string => {
  if (statuses.length === 0) return ''
  if (isControlQueued(statuses)) return OE_QUEUED_NARRATION
  const real = statuses.filter((s) => s.getTestString().trim())
  const running = real.filter(
    (s) => s.getRunStatus() === OeTestRunStatus.OE_TEST_RUNNING,
  )
  if (running.length) {
    const latest = running.reduce((a, b) =>
      b.getTestIdx() >= a.getTestIdx() ? b : a,
    )
    return getProcedureStatusLabel(latest)
  }
  return real.some(isTerminalStatus) ? 'Loading result…' : OE_QUEUED_NARRATION
}
